import { useEffect, useRef } from "react";
import type { Map as MaplibreMap, GeoJSONSource } from "maplibre-gl";
import { useNavigate } from "react-router";
import {
  toFeatureCollection,
  thermoStepExpression,
  mapCameraTarget,
  cssColor,
  type MapCameraTarget,
  type MapPoint,
} from "./directoryMapData";

// OpenFreeMap hosteado (gratis, sin llave, OSM/ODbL) — ver docs/PLAN_BLOQUE_4.md.
const STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";
const MEXICO_CENTER: [number, number] = [-102, 23];
const SOURCE_ID = "directory-points";
const FIT_PADDING = 40;
const FIT_MAX_ZOOM = 15;
const FIT_DURATION_MS = 400;

/** Reencuadra la cámara al conjunto de puntos — no-op si no hay ninguno válido. */
const applyCameraTarget = (map: MaplibreMap, target: MapCameraTarget) => {
  if (target.kind === "none") return;
  if (target.kind === "single") {
    map.flyTo({
      center: target.center,
      zoom: target.zoom,
      duration: FIT_DURATION_MS,
    });
    return;
  }
  map.fitBounds(target.bounds, {
    padding: FIT_PADDING,
    maxZoom: FIT_MAX_ZOOM,
    duration: FIT_DURATION_MS,
  });
};

/**
 * Mapa del directorio (Bloque 4 parte 1): lat/lng existentes, sin índice
 * espacial. `maplibre-gl` (y su CSS) se cargan con `import()` dentro del
 * efecto de montaje, para que el interruptor "Ver mapa" sea lo único que los
 * descarga — nadie paga ese peso al abrir el Directorio en vista de tabla.
 */
export const DirectoryMap = ({ rows }: { rows: MapPoint[] }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  // Última cámara aplicada (fingerprint del target), para reencuadrar solo
  // cuando cambia el CONJUNTO de puntos, no en cada re-render ni cuando el
  // usuario mueve/hace zoom manual (eso nunca llama applyCameraTarget).
  const cameraKeyRef = useRef<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    let map: MaplibreMap | undefined;

    (async () => {
      const [{ Map: MaplibreMapCtor, NavigationControl, setWorkerUrl }] =
        await Promise.all([
          import("maplibre-gl"),
          import("maplibre-gl/dist/maplibre-gl.css"),
        ]);
      if (cancelled || !containerRef.current) return;

      // In dev, maplibre-gl resolves its worker relative to its own
      // (node_modules) URL, which Vite serves fine. In production that
      // default would fetch cross-origin from a CDN; point it instead at
      // the same-origin copy the build places next to this chunk (see
      // `copyMaplibreWorkerFiles` in vite.config.ts).
      if (import.meta.env.PROD) {
        setWorkerUrl(new URL("maplibre-gl-worker.mjs", import.meta.url).href);
      }

      map = new MaplibreMapCtor({
        container: containerRef.current,
        style: STYLE_URL,
        center: MEXICO_CENTER,
        zoom: 4.2,
      });
      map.addControl(new NavigationControl(), "top-right");
      map.on("load", () => {
        if (!map) return;
        map.addSource(SOURCE_ID, {
          type: "geojson",
          data: toFeatureCollection(rowsRef.current),
        });
        map.addLayer({
          id: SOURCE_ID,
          type: "circle",
          source: SOURCE_ID,
          paint: {
            "circle-radius": 5,
            "circle-color": thermoStepExpression(cssColor),
            "circle-stroke-width": 1,
            "circle-stroke-color": "#ffffff",
          },
        });
        map.on("click", SOURCE_ID, (e) => {
          const id = e.features?.[0]?.properties?.id;
          if (id != null) navigate(`/directory/${id}/show`);
        });
        map.on("mouseenter", SOURCE_ID, () => {
          map!.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", SOURCE_ID, () => {
          map!.getCanvas().style.cursor = "";
        });

        const initialTarget = mapCameraTarget(rowsRef.current);
        cameraKeyRef.current = JSON.stringify(initialTarget);
        applyCameraTarget(map, initialTarget);
      });
      mapRef.current = map;
    })();

    return () => {
      cancelled = true;
      map?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const source = map?.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    source?.setData(toFeatureCollection(rows));

    if (!map) return;
    const target = mapCameraTarget(rows);
    const key = JSON.stringify(target);
    if (key === cameraKeyRef.current) return;
    cameraKeyRef.current = key;
    applyCameraTarget(map, target);
  }, [rows]);

  return (
    <div
      ref={containerRef}
      role="application"
      aria-label="Mapa del directorio"
      className="h-[70vh] w-full rounded-lg border"
    />
  );
};
